import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { customBaseUrl, httpGet, httpPost, httpGetPdf, httpPostFormData, httpPatch, httpGetBlob } from '../../store/http';
import { saveAs } from 'file-saver';
import { NotificationManager } from 'react-notifications';
import {
  Card,
  Button,
  Space,
  Modal,
  Input,
  Form,
  Checkbox,
  Table,
  Typography,
  Row,
  Col,
  Tag,
  Divider,
  Avatar,
  Descriptions,
  Alert,
  Upload,
  message,
  Select
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  UserOutlined,
  HistoryOutlined,
  DollarOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  UploadOutlined,
  LoadingOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import LoanDashboardLayout from '../../components/Layout/LoanDashboardLayout';
import LoanHistoryModal from '../../components/Modal/LoanHistoryModal';
import ApprovalHistoryModal from '../../components/Modal/ApprovalHistoryModal';
import moment from 'moment';
import AdminLayout from "../../components/Layout";
import { hideLoader, showLoader } from '../../utils/loader';
import slugs from '../../resources/slugs';
import { useNavigate } from 'react-router-dom';
import { useMatStyles, CardWrapper } from './style';

const { Title, Text } = Typography;
const { TextArea } = Input;

const LoanDetailsPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const classes = useMatStyles();
  const { from } = location.state || {};
  const [loanDetails, setLoanDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isOtherDataModalVisible, setIsOtherDataModalVisible] = useState(false);
  const [otherDataContent, setOtherDataContent] = useState({});
  const [isMandateModalVisible, setIsMandateModalVisible] = useState(false);
  const [mandateDetails, setMandateDetails] = useState(null);
  const [isMandateBalanceModalVisible, setIsMandateBalanceModalVisible] = useState(false);
  const [mandateBalance, setMandateBalance] = useState(null);
  const [mandateBalanceAmount, setMandateBalanceAmount] = useState(0);
  const [selectedLoanIdForHistory, setSelectedLoanIdForHistory] = useState(null);
  const [isLoanHistoryModalVisible, setIsLoanHistoryModalVisible] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [approvalHistory, setApprovalHistory] = useState(null);
  const [isApprovalHistoryModalVisible, setIsApprovalHistoryModalVisible] = useState(false);
  const [isGuarantorDetailsModalVisible, setIsGuarantorDetailsModalVisible] = useState(false);
  const [selectedGuarantorDetails, setSelectedGuarantorDetails] = useState(null);
  const [isVerifyOtpModalVisible, setIsVerifyOtpModalVisible] = useState(false);
  const [currentGuarantorAuthUserId, setCurrentGuarantorAuthUserId] = useState(null);
  const [otpInput, setOtpInput] = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);
  const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
  const [approvalChecklist, setApprovalChecklist] = useState({
    kyc_verified: false,
    id_verified: false,
    mandate_approved: false,
    mandate_balance_pass: false,
    guarantor_verified: false,
    employer_consent: false,
    address_verified: false,
    statement_of_account: false,
  });
  const [isVerificationModalVisible, setIsVerificationModalVisible] = useState(false);
  const [verificationDocs, setVerificationDocs] = useState({
    crc_report: '',
    home_verification_video_customer: '',
    home_verification_video_guarantor_1: '',
    home_verification_video_guarantor_2: '',
    interview_script: '',
    guarantor_1_crc_report: '',
    guarantor_2_crc_report: ''
  });
  const [uploading, setUploading] = useState({});

  const [isUpdateCycleModalVisible, setIsUpdateCycleModalVisible] = useState(false);
  const [updatingCycle, setUpdatingCycle] = useState(false);
  const [repaymentCycleValue, setRepaymentCycleValue] = useState(30);
  const [repaymentCycleUnit, setRepaymentCycleUnit] = useState('days');
  const [firstRepaymentDelayValue, setFirstRepaymentDelayValue] = useState('');
  const [firstRepaymentDelayUnit, setFirstRepaymentDelayUnit] = useState(undefined);

  useEffect(() => {
    const fetchLoanDetails = async () => {
      try {
        const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
        if (!token) {
          window.location.href = '/loan-admin-login';
          return;
        }
        showLoader();
        const res = await httpGet(
          `${customBaseUrl.loanUrl}/api/v1/admin/loans/${id}`,
          null,
          false,
          false,
          token
        );
        if (res.status) {
          setLoanDetails(res.data);
          if (res.data.verification_documents) {
            setVerificationDocs(prev => ({ ...prev, ...res.data.verification_documents }));
          }
        } else {
          NotificationManager.error(res.message || 'Failed to fetch loan details');
        }
      } catch (error) {
        console.error('Error fetching loan details:', error);
        NotificationManager.error('Error fetching loan details');
      } finally {
        setLoading(false);
        hideLoader();
      }
    };

    fetchLoanDetails();
  }, [id, from, refresh]);

  const handleExportDetails = async () => {
    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const blob = await httpGetBlob(
        `${customBaseUrl.loanUrl}/api/v1/admin/actions/${id}/export-details`,
        token
      );
      if (blob) {
        saveAs(blob, `Loan_Participant_Details_${id}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
        NotificationManager.success('Details exported successfully', 'Success');
      } else {
        NotificationManager.error('Failed to export details', 'Error');
      }
    } catch (error) {
      console.error('Export error:', error);
      NotificationManager.error('An error occurred during export', 'Error');
    } finally {
      hideLoader();
    }
  };

  const handleApproveSubmit = async () => {
    // 1. Checklist validation (Ant-design modal Ok button is also disabled based on this)
    const allChecked = Object.values(approvalChecklist).every(val => val === true);
    if (!allChecked) {
      NotificationManager.error('Please verify all checklist items before proceeding with approval');
      return;
    }

    // 2. Document validation (Prevent approval without actual file uploads)
    const requiredDocs = [
      { field: 'crc_report', label: 'CRC Report' },
      { field: 'home_verification_video_customer', label: 'Home Verification Video (Customer)' },
      { field: 'interview_script', label: 'Interview Script' }
    ];

    if (guarantorCount >= 1) {
      requiredDocs.push(
        { field: 'home_verification_video_guarantor_1', label: 'Home Verification Video (Guarantor 1)' },
        { field: 'guarantor_1_crc_report', label: 'Guarantor 1 CRC Report' }
      );
    }

    if (guarantorCount >= 2) {
      requiredDocs.push(
        { field: 'home_verification_video_guarantor_2', label: 'Home Verification Video (Guarantor 2)' },
        { field: 'guarantor_2_crc_report', label: 'Guarantor 2 CRC Report' }
      );
    }

    const missingDocs = requiredDocs.filter(doc => !verificationDocs[doc.field]);

    if (missingDocs.length > 0) {
      const missingLabels = missingDocs.map(d => d.label).join(', ');
      NotificationManager.error(`Cannot approve: Missing required documents (${missingLabels}). Please upload them first.`);
      return;
    }
    showLoader();
    try {
      const id = loanDetails.id;
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpPost(
        `${customBaseUrl.loanUrl}/api/v1/loans/${id}/admin-review`,
        {
          action: 'Approve',
          loan_admin_checklist: approvalChecklist,
        },
        false,
        token
      );
      if (res.status) {
        NotificationManager.success(res.message || 'Loan request approved successfully');
        setIsApproveModalVisible(false);
        setRefresh(prev => !prev);
      } else {
        NotificationManager.error(res.message || 'Failed to approve loan request');
      }
    } catch (error) {
      NotificationManager.error('An error occurred during approval');
    } finally {
      hideLoader();
    }
  };

  const handleUpdateRepaymentCycle = async () => {
    if (!repaymentCycleValue || repaymentCycleValue < 1) {
      NotificationManager.error('Repayment cycle value must be a positive integer');
      return;
    }
    if (!repaymentCycleUnit) {
      NotificationManager.error('Repayment cycle unit is required');
      return;
    }

    setUpdatingCycle(true);
    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const payload = {
        repayment_cycle_value: Number(repaymentCycleValue),
        repayment_cycle_unit: repaymentCycleUnit,
      };
      if (firstRepaymentDelayValue !== '') {
        payload.first_repayment_delay_value = Number(firstRepaymentDelayValue);
      }
      if (firstRepaymentDelayUnit) {
        payload.first_repayment_delay_unit = firstRepaymentDelayUnit;
      }

      const res = await httpPatch(
        `${customBaseUrl.loanUrl}/api/v1/admin/loans/${id}/repayment-cycle`,
        payload,
        null,
        false,
        token
      );

      if (res.status) {
        NotificationManager.success(res.message || 'Repayment cycle updated successfully');
        setIsUpdateCycleModalVisible(false);
        setRefresh(prev => !prev);
      } else {
        NotificationManager.error(res.message || 'Failed to update repayment cycle');
      }
    } catch (error) {
      NotificationManager.error(error.message || 'An error occurred during repayment cycle update');
    } finally {
      setUpdatingCycle(false);
      hideLoader();
    }
  };

  const handleResendGuarantorOtp = async (guarantorAuthUserId) => {
    setResendingOtp(true);
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpPost(
        `${customBaseUrl.loanUrl}/api/v1/loans/${id}/resend-guarantor-otp`,
        { guarantor_auth_user_id: guarantorAuthUserId },
        false,
        token
      );
      if (res.status) {
        NotificationManager.success(res.message || "OTP resent successfully!");
      } else {
        NotificationManager.error(res.message || "Failed to resend OTP.");
      }
    } catch (error) {
      NotificationManager.error("Error resending OTP.");
    } finally {
      setResendingOtp(false);
    }
  };

  const handleVerifyGuarantorOtp = async () => {
    if (!otpInput) {
      NotificationManager.warning("Please enter the OTP.");
      return;
    }
    setVerifyingOtp(true);
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpPost(
        `${customBaseUrl.loanUrl}/api/v1/loans/${id}/verify-otp`,
        {
          otp: otpInput,
          guarantor_auth_user_id: currentGuarantorAuthUserId
        },
        false,
        token
      );
      if (res.status) {
        NotificationManager.success(res.message || "Guarantor OTP verified successfully!");
        setIsVerifyOtpModalVisible(false);
        setOtpInput('');
        setRefresh(prev => !prev);
      } else {
        NotificationManager.error(res.message || "Invalid OTP.");
      }
    } catch (error) {
      NotificationManager.error("Error verifying OTP.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleVerifyGuarantor = async (accountNumber) => {
    if (!accountNumber) {
      NotificationManager.warning("No account number found for this guarantor.");
      return;
    }
    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpGet(
        `${customBaseUrl.loanUrl}/api/v1/admin/users/account/${accountNumber}`,
        null,
        false,
        false,
        token
      );
      if (res.status && res.data) {
        setSelectedGuarantorDetails(res.data);
        setIsGuarantorDetailsModalVisible(true);
        NotificationManager.success("Guarantor account details fetched successfully!");
      } else {
        NotificationManager.error(res.message || "Failed to fetch guarantor details.");
      }
    } catch (error) {
      NotificationManager.error("Error fetching guarantor details.");
    } finally {
      hideLoader();
    }
  };

  const handlePreviewOfferLetter = async () => {
    try {
      showLoader();
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpGetPdf(
        `${customBaseUrl.loanUrl}/api/v1/loans/${id}/preview-offer-letter`,
        null,
        false,
        false,
        token
      );

      const blob = new Blob([res], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `loan-offer-preview-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      NotificationManager.error('Error fetching offer letter preview');
    } finally {
      hideLoader();
    }
  };

  const handleRejectSubmit = async () => {
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      showLoader();
      let res;
      if (loanDetails.status === 'Offer Letter Signed' || loanDetails.status === 'Offer Letter Rejected') {
        res = await httpPost(
          `${customBaseUrl.loanUrl}/api/v1/admin/actions/${id}/reject-offer-letter`,
          { rejection_reason: rejectionReason },
          false,
          token
        );
      } else {
        res = await httpPost(
          `${customBaseUrl.loanUrl}/api/v1/loans/${id}/admin-review`,
          {
            action: 'Reject',
            rejection_reason: rejectionReason,
            loan_admin_checklist: approvalChecklist
          },
          false,
          token
        );
      }

      if (res.status) {
        NotificationManager.success('Loan rejected successfully');
        setIsRejectModalVisible(false);
        setRejectionReason('');
        setRefresh(!refresh);
      } else {
        NotificationManager.error(res.message || 'Failed to reject loan');
      }
    } catch (error) {
      NotificationManager.error('Error rejecting loan');
    } finally {
      hideLoader();
    }
  };

  const handleViewMandate = async () => {
    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpGet(
        `${customBaseUrl.loanUrl}/api/v1/admin/mandates/${loanDetails.mandate_id}`,
        null,
        false,
        false,
        token
      );
      if (res.status) {
        setMandateDetails(res.data);
        setIsMandateModalVisible(true);
      }
    } finally {
      hideLoader();
    }
  };

  const handleFileUpload = async (file, field, docType) => {
    // Validation: Home Verification must be Video
    if (field.startsWith('home_verification_video')) {
      if (!file.type.startsWith('video/')) {
        NotificationManager.error('Invalid file type. Home verification must be a video file.');
        return false;
      }
    }

    // Validation: Interview Script must be Video or Audio
    if (field === 'interview_script') {
      if (!file.type.startsWith('video/') && !file.type.startsWith('audio/')) {
        NotificationManager.error('Invalid file type. Interview script must be a video or audio file.');
        return false;
      }
    }

    setUploading(prev => ({ ...prev, [field]: true }));
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const formData = new FormData();
      formData.append('file', file);

      const username = loanDetails.user?.email || 'unknown';
      const uploadUrl = `${customBaseUrl.authUrl}/files/upload?documentType=${docType}&username=${username}&folderName=loan`;

      const res = await httpPostFormData(uploadUrl, formData, null, false, token);

      if (res.success && res.data?.fileUrl) {
        setVerificationDocs(prev => ({ ...prev, [field]: res.data.fileUrl }));
        NotificationManager.success(`${field.replace(/_/g, ' ')} uploaded successfully`);
      } else {
        NotificationManager.error(res.message || `Failed to upload ${field.replace(/_/g, ' ')}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      NotificationManager.error(`Error uploading ${field.replace(/_/g, ' ')}`);
    } finally {
      setUploading(prev => ({ ...prev, [field]: false }));
    }
    return false; // Prevent default upload
  };

  const handleVerificationSubmit = async () => {
    // Filter out empty values to prevent backend validation errors
    const filteredDocs = Object.entries(verificationDocs).reduce((acc, [key, value]) => {
      // Include only non-empty values
      if (value && (Array.isArray(value) ? value.length > 0 : true)) {
        acc[key] = value;
      }
      return acc;
    }, {});

    if (Object.keys(filteredDocs).length === 0) {
      NotificationManager.warning('Please upload at least one document before submitting.');
      return;
    }

    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpPatch(
        `${customBaseUrl.loanUrl}/api/v1/admin/actions/${id}/verification-documents`,
        filteredDocs,
        null,
        false,
        token
      );

      if (res.status) {
        NotificationManager.success(res.message || 'Verification documents submitted successfully');
        setIsVerificationModalVisible(false);
        setRefresh(prev => !prev);
      } else {
        NotificationManager.error(res.message || 'Failed to submit verification documents');
      }
    } catch (error) {
      console.error('Submission error:', error);
      NotificationManager.error('Error submitting verification documents');
    } finally {
      hideLoader();
    }
  };

  const handleViewApprovalHistory = async () => {
    showLoader();
    try {
      const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
      const res = await httpGet(
        `${customBaseUrl.loanUrl}/api/v1/loans/${id}/history`,
        null,
        false,
        false,
        token
      );
      if (res) {
        setApprovalHistory(res);
        setIsApprovalHistoryModalVisible(true);
      }
    } finally {
      hideLoader();
    }
  };

  const Layout = from === "admin" ? AdminLayout : LoanDashboardLayout;

  if (loading || !loanDetails) {
    return <Layout>{loading ? 'Loading...' : 'Loan not found.'}</Layout>;
  }

  const guarantorCount = (loanDetails.guarantors?.length > 0 ? loanDetails.guarantors : loanDetails.guarantor_details)?.length || 0;

  const InfoRow = ({ label, value, color }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
      <Text type="secondary" style={{ fontSize: 13 }}>{label}</Text>
      <Text strong style={{ color: color || '#1a1a1a' }}>{value}</Text>
    </div>
  );

  return (
    <Layout>
      <div style={{ padding: '0 32px 32px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
          <div>
            <Title level={2} style={{ margin: 0, fontWeight: 800 }}>Loan Request Details</Title>
            <Space>
              <Tag color="blue" style={{ borderRadius: 6 }}>ID: {loanDetails.id}</Tag>
              <Tag color={loanDetails.status === 'Approved' ? 'green' : 'orange'} style={{ borderRadius: 6 }}>{loanDetails.status}</Tag>
            </Space>
          </div>
          <Space>
            {loanDetails.loan_admin_approval_status !== "Approved" &&
              loanDetails.loan_admin_approval_status !== "Rejected" && (
                <>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    style={{ height: 44, borderRadius: 10, background: '#27AE60', border: 'none' }}
                    onClick={() => setIsApproveModalVisible(true)}
                  >
                    Approve
                  </Button>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    style={{ height: 44, borderRadius: 10 }}
                    onClick={() => setIsRejectModalVisible(true)}
                  >
                    Reject
                  </Button>
                </>
              )}
            {loanDetails.loan_admin_approval_status === "Approved" &&
              loanDetails.loan_officer_approval_status === "Approved" &&
              loanDetails.finance_approver_approval_status === "Approved" && (
                <Button
                  icon={<FileTextOutlined />}
                  style={{ height: 44, borderRadius: 10, background: '#0086E8', color: '#fff', border: 'none' }}
                  onClick={handlePreviewOfferLetter}
                >
                  Preview Offer Letter
                </Button>
              )}
            {(loanDetails.offer_letter_document || loanDetails.offer_url) && (
              <Button
                icon={<FileTextOutlined />}
                style={{ height: 44, borderRadius: 10, background: '#52c41a', color: '#fff', border: 'none' }}
                onClick={() => window.open(loanDetails.offer_letter_document || loanDetails.offer_url, '_blank')}
              >
                View Signed Offer Letter
              </Button>
            )}
          </Space>
        </div>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
              {/* User Summary Card */}
              <CardWrapper>
                <Card style={{ borderRadius: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24 }}>
                    <Avatar size={64} src={loanDetails.user?.other_data?.profilePictureUrl} icon={<UserOutlined />} style={{ backgroundColor: '#FF4B01' }} />
                    <div>
                      <Title level={4} style={{ margin: 0 }}>{loanDetails.user?.other_data?.fullName || `${loanDetails.user?.first_name} ${loanDetails.user?.last_name}`}</Title>
                      <Text type="secondary">{loanDetails.user?.email} • {loanDetails.user?.phone}</Text>
                    </div>
                  </div>
                  <Divider style={{ margin: '16px 0' }} />
                  <Row gutter={40}>
                    <Col span={12}>
                      <InfoRow label="Tier" value={loanDetails.user?.other_data?.tierDisplayName || `Level ${loanDetails.user?.kyc_level}`} />
                      <InfoRow label="KYC Status" value={loanDetails.user?.other_data?.kycStatus} />
                      <InfoRow label="BVN" value={loanDetails.user?.bvn} />
                      <InfoRow label="Gender" value={loanDetails.user?.other_data?.gender} />
                    </Col>
                    <Col span={12}>
                      <InfoRow label="Location" value={`${loanDetails.user?.other_data?.city}, ${loanDetails.user?.other_data?.state}`} />
                      <InfoRow label="User Type" value={loanDetails.user?.other_data?.userType} />
                      <InfoRow label="Account Status" value={<Tag color={loanDetails.user?.other_data?.status === 'ACTIVE' ? 'success' : 'error'}>{loanDetails.user?.other_data?.status}</Tag>} />
                      <Button type="link" style={{ padding: 0 }} onClick={() => setIsOtherDataModalVisible(true)}>View Full Profile</Button>
                    </Col>
                  </Row>
                </Card>
              </CardWrapper>

              {/* Installments Table */}
              {loanDetails.installment_dates?.length > 0 && (
                <Card title={<Space><DollarOutlined /> Payment Schedule</Space>} style={{ borderRadius: 16 }}>
                  <Table
                    dataSource={loanDetails.installment_dates.map((item, index) => ({
                      key: index,
                      date: moment(item.date).format('YYYY-MM-DD HH:mm:ss'),
                      amount: `₦${Number(item.amount).toLocaleString()}`,
                    }))}
                    columns={[
                      { title: 'Payment Date', dataIndex: 'date', key: 'date' },
                      { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right' },
                    ]}
                    pagination={false}
                    size="small"
                  />
                  {(loanDetails.status === 'Disbursed' || loanDetails.status === 'Completed') && (
                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                      <Button
                        type="primary"
                        onClick={() => {
                          setRepaymentCycleValue(loanDetails.repayment_cycle_value || 30);
                          setRepaymentCycleUnit(loanDetails.repayment_cycle_unit || 'days');
                          setFirstRepaymentDelayValue(loanDetails.first_repayment_delay_value || '');
                          setFirstRepaymentDelayUnit(loanDetails.first_repayment_delay_unit || undefined);
                          setIsUpdateCycleModalVisible(true);
                        }}
                      >
                        Update Repayment Cycle
                      </Button>
                    </div>
                  )}
                </Card>
              )}

              {/* Employment & Bank Details */}
              <Row gutter={24}>
                <Col span={12}>
                  <Card title={<Space><FileTextOutlined /> Employment</Space>} style={{ borderRadius: 16, height: '100%' }}>
                    <InfoRow label="Employer" value={loanDetails.employment_details?.employer_name} />
                    <InfoRow label="Company" value={loanDetails.employment_details?.company_name} />
                    <InfoRow label="Company Address" value={loanDetails.employment_details?.company_address} />
                    <InfoRow label="Salary" value={`₦${loanDetails.employment_details?.salary?.toLocaleString()}`} />
                    <InfoRow label="Occupation" value={loanDetails.employment_details?.occupation_type} />
                    <Divider style={{ margin: '12px 0' }} />
                    <InfoRow label="HR Name" value={loanDetails.employment_details?.hr_name || 'N/A'} />
                    <InfoRow label="HR Email" value={loanDetails.employment_details?.hr_email || 'N/A'} />
                    <InfoRow label="HR Phone" value={loanDetails.employment_details?.hr_phone || 'N/A'} />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title={<Space><BankOutlined /> Bank Information</Space>} style={{ borderRadius: 16, height: '100%' }}>
                    {loanDetails.repayment_details ? (
                      <div>
                        <InfoRow label="Bank" value={loanDetails.repayment_details.institution?.name} />
                        <InfoRow label="A/C Name" value={loanDetails.repayment_details.account_name} />
                        <InfoRow label="A/C No" value={loanDetails.repayment_details.account_number} />
                        <InfoRow label="A/C Type" value={loanDetails.repayment_details.account_type} />
                      </div>
                    ) : loanDetails.account_details?.map((acc, i) => (
                      <div key={i} style={{ marginBottom: i === 0 ? 12 : 0 }}>
                        <InfoRow label="Bank" value={acc?.institution?.name} />
                        <InfoRow label="A/C No" value={acc?.account_number} />
                      </div>
                    ))}
                  </Card>
                </Col>
              </Row>

              {(loanDetails.guarantors?.length > 0 ? loanDetails.guarantors : loanDetails.guarantor_details)?.length > 0 && (
                <Card title={<Space><UserOutlined /> Guarantor Information</Space>} style={{ borderRadius: 16 }}>
                  {(loanDetails.guarantors?.length > 0 ? loanDetails.guarantors : loanDetails.guarantor_details).map((guarantor, index) => {
                    const richData = guarantor.other_data || {};
                    const authUserId = richData.id || guarantor.auth_user_id;
                    const otpStatus = loanDetails.guarantor_details?.find(g => g.auth_user_id == authUserId)?.guarantor_otp_status || guarantor.guarantor_otp_status || 'Pending';
                    const accountNumber = richData.primaryAccountNumber || guarantor.account_number;

                    return (
                      <div key={index} style={{ marginBottom: index === (loanDetails.guarantors?.length || loanDetails.guarantor_details?.length) - 1 ? 0 : 24 }}>
                        <Row gutter={24} align="middle">
                          <Col span={2}>
                            <Avatar size={48} src={richData.profilePictureUrl} icon={<UserOutlined />} />
                          </Col>
                          <Col span={7}>
                            <InfoRow label="Full Name" value={richData.fullName || guarantor.name || `${guarantor.first_name} ${guarantor.last_name}`} />
                            <InfoRow label="WAYA Account" value={accountNumber} />
                          </Col>
                          <Col span={7}>
                            <InfoRow label="Tier" value={richData.tierDisplayName || `Lvl ${guarantor.kyc_level || 'N/A'}`} />
                            <InfoRow label="OTP Status" value={<Tag color={otpStatus === 'Approved' ? 'success' : otpStatus === 'Pending' ? 'orange' : 'error'}>{otpStatus}</Tag>} />
                          </Col>
                          <Col span={8} style={{ textAlign: 'right' }}>
                            <Space split={<Divider type="vertical" />}>
                              <Button
                                type="primary"
                                size="small"
                                ghost
                                onClick={() => handleVerifyGuarantor(accountNumber)}
                              >
                                View Detailed
                              </Button>
                              {otpStatus !== 'Verified' && otpStatus !== 'Approved' && (
                                <Button
                                  type="primary"
                                  size="small"
                                  onClick={() => {
                                    setCurrentGuarantorAuthUserId(authUserId);
                                    setIsVerifyOtpModalVisible(true);
                                  }}
                                >
                                  Verify OTP
                                </Button>
                              )}
                            </Space>
                          </Col>
                        </Row>
                        {index < (loanDetails.guarantors?.length || loanDetails.guarantor_details?.length) - 1 && <Divider style={{ margin: '12px 0' }} />}
                      </div>
                    );
                  })}
                </Card>
              )}
            </Space>
          </Col>

          <Col xs={24} lg={8}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
              <Card title="Loan Summary" style={{ borderRadius: 16 }}>
                <div style={{ textAlign: 'center', marginBottom: 24, padding: '16px', background: '#f8f9fa', borderRadius: 12 }}>
                  <Text type="secondary" block>Amount Requested</Text>
                  <Title level={2} style={{ margin: 0, color: '#FF4B01' }}>₦{loanDetails.amount?.toLocaleString()}</Title>
                </div>
                <InfoRow label="Type" value={loanDetails.type} />
                <InfoRow label="Term" value={`${loanDetails.term_months} Months`} color="#0086E8" />
                <InfoRow label="Needed By" value={loanDetails.needed_by} />
                <InfoRow label="Created" value={moment(loanDetails.created_at).format('MMM Do YYYY')} />
              </Card>

              <Card title="Administrative Actions" style={{ borderRadius: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button block icon={<HistoryOutlined />} onClick={handleViewApprovalHistory}>Approval History</Button>
                  <Button block icon={<SafetyCertificateOutlined />} onClick={handleViewMandate}>View Mandate Details</Button>
                  <Button block icon={<FileTextOutlined />} onClick={() => { setSelectedLoanIdForHistory(id); setIsLoanHistoryModalVisible(true); }}>Full Loan History</Button>
                  <Button block icon={<UploadOutlined />} onClick={() => setIsVerificationModalVisible(true)}>Upload Verification Docs</Button>
                  <Button block icon={<DownloadOutlined />} onClick={handleExportDetails}>Download Profile Details (Excel)</Button>
                  {(loanDetails.offer_letter_document || loanDetails.offer_url) && (
                    <Button
                      block
                      type="primary"
                      style={{ background: '#52c41a', borderColor: '#52c41a' }}
                      icon={<FileTextOutlined />}
                      onClick={() => window.open(loanDetails.offer_letter_document || loanDetails.offer_url, '_blank')}
                    >
                      View Signed Offer Letter
                    </Button>
                  )}
                  {/* <Button block icon={<SearchOutlined />} onClick={() => navigate(slugs.loan_audit_logs, { state: { entityId: id } })}>Audit Logs</Button> */}
                  {loanDetails.files?.account_statement_url && (
                    <Button block type="dashed" href={loanDetails.files.account_statement_url} target="_blank">
                      View Bank Statement
                    </Button>
                  )}
                </Space>
              </Card>

              <Card title="Verification Documents" style={{ borderRadius: 16 }}>
                {loanDetails.verification_documents && Object.values(loanDetails.verification_documents).some(v => v) ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {Object.entries(loanDetails.verification_documents)
                      .filter(([key, value]) => {
                        const isGuarantor2Doc = key === 'guarantor_2_crc_report' || key === 'home_verification_video_guarantor_2';
                        if (isGuarantor2Doc) return value && guarantorCount > 1;
                        return value;
                      })
                      .map(([key, value]) => (
                        <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <Text style={{ fontSize: 13 }}>{key.replace(/_/g, ' ')}</Text>
                          <Button
                            type="link"
                            size="small"
                            icon={<FileTextOutlined />}
                            href={value}
                            target="_blank"
                          >
                            View
                          </Button>
                        </div>
                      ))}
                  </Space>
                ) : (
                  <Text type="secondary" italic>No verification documents uploaded yet.</Text>
                )}
              </Card>

              {loanDetails.loan_admin_checklist && (
                <Card title="Admin Checklist" style={{ borderRadius: 16 }}>
                  {Object.entries(loanDetails.loan_admin_checklist).map(([key, value]) => (
                    <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <Text style={{ fontSize: 12 }}>{key.replace(/_/g, ' ')}</Text>
                      {value ? <Tag color="success">Verified</Tag> : <Tag color="default">Pending</Tag>}
                    </div>
                  ))}
                </Card>
              )}

              {loanDetails.booked_by_details && (
                <Card title={<Space><UserOutlined /> Booked By</Space>} style={{ borderRadius: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <Avatar size="small" icon={<UserOutlined />} />
                    <div>
                      <Text strong block>{loanDetails.booked_by_details.first_name} {loanDetails.booked_by_details.last_name}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>{loanDetails.booked_by_details.email}</Text>
                    </div>
                  </div>
                  <InfoRow label="Booking Date" value={moment(loanDetails.created_at).format('LLL')} />
                </Card>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      {/* Modals */}
      <Modal
        title="Approve Loan Request"
        visible={isApproveModalVisible}
        onOk={handleApproveSubmit}
        onCancel={() => setIsApproveModalVisible(false)}
        okText="Confirm Approval"
        width={600}
        okButtonProps={{ disabled: !Object.values(approvalChecklist).every(v => v) }}
      >
        <Form layout="vertical">
          <Text type="secondary">Please confirm you have verified the following items:</Text>
          <div style={{ marginTop: 16 }}>
            {Object.keys(approvalChecklist).map(key => (
              <Form.Item key={key} style={{ marginBottom: 8 }}>
                <Checkbox
                  checked={approvalChecklist[key]}
                  onChange={(e) => setApprovalChecklist({ ...approvalChecklist, [key]: e.target.checked })}
                >
                  {key.replace(/_/g, ' ')}
                </Checkbox>
              </Form.Item>
            ))}
          </div>
        </Form>
      </Modal>

      <Modal
        title="Reject Loan Request"
        visible={isRejectModalVisible}
        onOk={handleRejectSubmit}
        onCancel={() => setIsRejectModalVisible(false)}
        okText="Confirm Rejection"
        okButtonProps={{ danger: true }}
      >
        <Form layout="vertical">
          <Form.Item label="Reason for Rejection" required>
            <TextArea rows={4} value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} placeholder="Please provide a clear reason for rejection..." />
          </Form.Item>
        </Form>
      </Modal>

      <LoanHistoryModal showModal={isLoanHistoryModalVisible} hideModal={() => setIsLoanHistoryModalVisible(false)} loanId={id} />

      <Modal
        title="Guarantor Account Verification"
        visible={isGuarantorDetailsModalVisible}
        onCancel={() => setIsGuarantorDetailsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsGuarantorDetailsModalVisible(false)}>Close</Button>,
          <Button key="confirm" type="primary" onClick={() => {
            setApprovalChecklist({ ...approvalChecklist, guarantor_verified: true });
            setIsGuarantorDetailsModalVisible(false);
            NotificationManager.info("Guarantor marked as verified in checklist.");
          }}>Confirm & Mark Verified</Button>
        ]}
        width={500}
      >
        {selectedGuarantorDetails && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Account Name">{selectedGuarantorDetails.accountName || selectedGuarantorDetails.fullName}</Descriptions.Item>
            <Descriptions.Item label="Account Number">{selectedGuarantorDetails.accountNo || selectedGuarantorDetails.accountNumber}</Descriptions.Item>
            <Descriptions.Item label="Balance">₦{selectedGuarantorDetails.balance?.toLocaleString()}</Descriptions.Item>
            <Descriptions.Item label="Phone Number">{selectedGuarantorDetails.phoneNumber}</Descriptions.Item>
            <Descriptions.Item label="Email">{selectedGuarantorDetails.email}</Descriptions.Item>
            <Descriptions.Item label="Tier">
              <Tag color="blue">{selectedGuarantorDetails.tierDisplayName || selectedGuarantorDetails.tierName || 'N/A'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={selectedGuarantorDetails.status === 'ACTIVE' ? 'success' : 'error'}>{selectedGuarantorDetails.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="KYC Status">
              <Tag color={selectedGuarantorDetails.kycStatus === 'VERIFIED' ? 'success' : 'warning'}>{selectedGuarantorDetails.kycStatus}</Tag>
            </Descriptions.Item>
          </Descriptions>
        )}
        <div style={{ marginTop: 16 }}>
          <Alert
            message="Cross-check these details with the provided guarantor information."
            type="info"
            showIcon
          />
        </div>
      </Modal>

      <ApprovalHistoryModal
        visible={isApprovalHistoryModalVisible}
        onCancel={() => setIsApprovalHistoryModalVisible(false)}
        historyData={approvalHistory}
      />

      {/* Profile Detail Modal */}
      <Modal
        title="Full User Profile"
        visible={isOtherDataModalVisible}
        onCancel={() => setIsOtherDataModalVisible(false)}
        footer={null}
        width={800}
      >
        <Descriptions bordered column={2}>
          {Object.entries(loanDetails.user?.other_data || {})
            .filter(([key, val]) => typeof val !== 'object')
            .map(([key, value]) => (
              <Descriptions.Item label={key} key={key}>{String(value)}</Descriptions.Item>
            ))
          }
        </Descriptions>
      </Modal>

      {/* Mandate Modal */}
      <Modal title="Mandate Details" visible={isMandateModalVisible} onCancel={() => setIsMandateModalVisible(false)} footer={null} width={800}>
        {mandateDetails && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions bordered column={2} title="General Information">
              <Descriptions.Item label="Mandate ID">{mandateDetails.id}</Descriptions.Item>
              <Descriptions.Item label="Reference">{mandateDetails.reference}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={mandateDetails.status === 'approved' ? 'green' : 'orange'}>
                  {mandateDetails.status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Verification Method">{mandateDetails.verification_method?.replace(/_/g, ' ')}</Descriptions.Item>
              <Descriptions.Item label="Amount">₦{(mandateDetails.amount / 100).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Balance">₦{(mandateDetails.balance / 100).toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Start Date">{moment(mandateDetails.start_date).format('LL')}</Descriptions.Item>
              <Descriptions.Item label="End Date">{moment(mandateDetails.end_date).format('LL')}</Descriptions.Item>
            </Descriptions>

            <Descriptions bordered column={2} title="Account Details">
              <Descriptions.Item label="Account Name">{mandateDetails.account_name}</Descriptions.Item>
              <Descriptions.Item label="Account Number">{mandateDetails.account_number}</Descriptions.Item>
              <Descriptions.Item label="Bank">{mandateDetails.institution?.name}</Descriptions.Item>
              <Descriptions.Item label="Bank Code">{mandateDetails.institution?.bank_code}</Descriptions.Item>
              <Descriptions.Item label="NIP Code">{mandateDetails.institution?.nip_code}</Descriptions.Item>
              <Descriptions.Item label="NIBSS Code">{mandateDetails.nibss_code}</Descriptions.Item>
            </Descriptions>

            <Descriptions bordered column={2} title="Configuration & Status">
              <Descriptions.Item label="Mandate Type">{mandateDetails.mandate_type?.toUpperCase()}</Descriptions.Item>
              <Descriptions.Item label="Debit Type">{mandateDetails.debit_type?.toUpperCase()}</Descriptions.Item>
              <Descriptions.Item label="Approved">
                {mandateDetails.approved ? <Tag color="success">YES</Tag> : <Tag color="error">NO</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Ready to Debit">
                {mandateDetails.ready_to_debit ? <Tag color="success">YES</Tag> : <Tag color="error">NO</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Live Mode">
                {mandateDetails.live_mode ? <Tag color="blue">LIVE</Tag> : <Tag color="default">TEST</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Created Date">{moment(mandateDetails.date).format('LLL')}</Descriptions.Item>
              <Descriptions.Item label="Narration" span={2}>{mandateDetails.narration}</Descriptions.Item>
            </Descriptions>

            {mandateDetails.linked_mandates?.length > 0 && (
              <Card title="Linked Mandates" size="small">
                <Table
                  dataSource={mandateDetails.linked_mandates}
                  pagination={false}
                  size="small"
                  columns={[
                    { title: 'Account Name', dataIndex: 'account_name', key: 'account_name' },
                    { title: 'Account Number', dataIndex: 'account_number', key: 'account_number' },
                    { title: 'Bank', dataIndex: 'bank_name', key: 'bank_name' },
                    {
                      title: 'Ready',
                      dataIndex: 'is_ready_to_debit',
                      key: 'is_ready_to_debit',
                      render: (ready) => ready ? <Tag color="success">YES</Tag> : <Tag color="error">NO</Tag>
                    },
                  ]}
                />
              </Card>
            )}
          </Space>
        )}
      </Modal>
      {/* OTP Verification Modal */}
      <Modal
        title="Verify Guarantor OTP"
        visible={isVerifyOtpModalVisible}
        onOk={handleVerifyGuarantorOtp}
        onCancel={() => {
          setIsVerifyOtpModalVisible(false);
          setOtpInput('');
        }}
        confirmLoading={verifyingOtp}
        okText="Verify OTP"
      >
        <p>Enter the 6-digit OTP sent to the guarantor.</p>
        <Input
          placeholder="Enter OTP"
          maxLength={6}
          value={otpInput}
          onChange={e => setOtpInput(e.target.value)}
          autoFocus
        />
        <div style={{ marginTop: 12, textAlign: 'right' }}>
          <Button
            type="link"
            size="small"
            loading={resendingOtp}
            onClick={() => handleResendGuarantorOtp(currentGuarantorAuthUserId)}
          >
            Resend OTP
          </Button>
        </div>
      </Modal>

      {/* Verification Documents Upload Modal */}
      <Modal
        title="Upload Verification Documents"
        visible={isVerificationModalVisible}
        onOk={handleVerificationSubmit}
        onCancel={() => setIsVerificationModalVisible(false)}
        okText="Submit All Documents"
        width={700}
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
          <Row gutter={[16, 24]}>
            <Col span={24}>
              <Divider orientation="left" style={{ margin: '0 0 16px 0' }}>Verification Action Items</Divider>
            </Col>
            {[
              { label: 'CRC Report', field: 'crc_report', docType: 'CRC_CUSTOMER' },
              { label: 'Home Verification Video (Customer)', field: 'home_verification_video_customer', docType: 'HOME_VERIFICATION_VIDEO_CUSTOMER' },
              { label: 'Home Verification Video (Guarantor 1)', field: 'home_verification_video_guarantor_1', docType: 'HOME_VERIFICATION_VIDEO_GUARANTOR_1' },
              { label: 'Home Verification Video (Guarantor 2)', field: 'home_verification_video_guarantor_2', docType: 'HOME_VERIFICATION_VIDEO_GUARANTOR_2' },
              { label: 'Interview Script', field: 'interview_script', docType: 'INTERVIEW_SCRIPT' },
              { label: 'Guarantor 1 CRC Report', field: 'guarantor_1_crc_report', docType: 'CRC_GUARANTOR_1' },
              { label: 'Guarantor 2 CRC Report', field: 'guarantor_2_crc_report', docType: 'CRC_GUARANTOR_2' },
            ].filter(doc => {
              if (doc.field === 'home_verification_video_guarantor_2' || doc.field === 'guarantor_2_crc_report') {
                return guarantorCount > 1;
              }
              return true;
            }).map((doc) => (
              <Col span={24} key={doc.field}>
                <Card size="small" title={doc.label} style={{ borderRadius: 8 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Upload
                      beforeUpload={(file) => handleFileUpload(file, doc.field, doc.docType)}
                      showUploadList={false}
                      disabled={uploading[doc.field]}
                      accept={
                        doc.field.startsWith('home_verification_video') ? 'video/*' :
                          doc.field === 'interview_script' ? 'video/*,audio/*' :
                            undefined
                      }
                    >
                      <Button
                        icon={uploading[doc.field] ? <LoadingOutlined /> : <UploadOutlined />}
                        loading={uploading[doc.field]}
                        type={verificationDocs[doc.field] ? 'default' : 'primary'}
                        ghost={!verificationDocs[doc.field]}
                      >
                        {verificationDocs[doc.field] ? 'Change Document' : 'Select File to Upload'}
                      </Button>
                    </Upload>
                    {verificationDocs[doc.field] && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                        <Tag color="success" icon={<CheckCircleOutlined />}>Uploaded</Tag>
                        <Text type="secondary" ellipsis style={{ maxWidth: 350 }}>
                          {verificationDocs[doc.field]}
                        </Text>
                        <Button
                          type="link"
                          icon={<DownloadOutlined />}
                          href={verificationDocs[doc.field]}
                          target="_blank"
                          size="small"
                        >
                          View
                        </Button>
                      </div>
                    )}
                  </Space>
                </Card>
              </Col>
            ))}

            {/* Additional Existing Verification Documents (not in standard list) */}
            {Object.keys(verificationDocs)
              .filter(key => verificationDocs[key] && ![
                'crc_report', 'home_verification_video_customer', 'home_verification_video_guarantor_1',
                'home_verification_video_guarantor_2', 'interview_script', 'guarantor_1_crc_report', 'guarantor_2_crc_report'
              ].includes(key))
              .map((key) => (
                <Col span={24} key={key}>
                  <Card size="small" title={key.replace(/_/g, ' ').toUpperCase()} style={{ borderRadius: 8, borderLeft: '4px solid #52c41a' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Tag color="success" icon={<CheckCircleOutlined />}>Uploaded</Tag>
                      <Text type="secondary" ellipsis style={{ maxWidth: 350 }}>
                        {verificationDocs[key]}
                      </Text>
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        href={verificationDocs[key]}
                        target="_blank"
                        size="small"
                      >
                        View
                      </Button>
                    </div>
                  </Card>
                </Col>
              ))}

          </Row>
        </div>
      </Modal>

      <Modal
        title="Update Loan Repayment Cycle"
        visible={isUpdateCycleModalVisible}
        onOk={handleUpdateRepaymentCycle}
        onCancel={() => setIsUpdateCycleModalVisible(false)}
        confirmLoading={updatingCycle}
        okText="Update Cycle"
        width={500}
      >
        <Form layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Repayment Cycle Value" required>
                <Input
                  type="number"
                  min={1}
                  value={repaymentCycleValue}
                  onChange={(e) => setRepaymentCycleValue(Number(e.target.value))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Repayment Cycle Unit" required>
                <Select
                  value={repaymentCycleUnit}
                  onChange={(val) => setRepaymentCycleUnit(val)}
                  options={[
                    { value: 'hours', label: 'Hours' },
                    { value: 'days', label: 'Days' },
                    { value: 'weeks', label: 'Weeks' },
                    { value: 'months', label: 'Months' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="First Repayment Delay Value">
                <Input
                  type="number"
                  min={1}
                  value={firstRepaymentDelayValue}
                  onChange={(e) => setFirstRepaymentDelayValue(e.target.value ? Number(e.target.value) : '')}
                  placeholder="Optional"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="First Repayment Delay Unit">
                <Select
                  value={firstRepaymentDelayUnit}
                  onChange={(val) => setFirstRepaymentDelayUnit(val)}
                  placeholder="Optional"
                  allowClear
                  options={[
                    { value: 'hours', label: 'Hours' },
                    { value: 'days', label: 'Days' },
                    { value: 'weeks', label: 'Weeks' },
                    { value: 'months', label: 'Months' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Layout>
  );
};

export default LoanDetailsPage;
